import axios from 'axios';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import useSWR from 'swr';
import { Button } from '@mui/material';
import { GridColDef, GridSortModel } from '@mui/x-data-grid-pro';
import { GridSelectionModel } from '@mui/x-data-grid/models/gridSelectionModel';

import {
  formatPhoneNumber,
  gridDateTimeFormatter,
  withQueryParams,
} from '@shared/commonFunctions';
import { useUsers } from '@shared/hooks/useUsers';
import { dataGridCol } from '@shared/components/grid/common';
import { useToggle } from '@shared/hooks/useToggle';
import { PhoenixGrid } from '@shared/components/grid/PhoenixGrid';
import { SERVICES } from '@shared/constants/apiConfig';
import { Notistack } from '@shared/components/Notistack/Notistack';
import { ISelectOption } from '@shared/inputs/Select';
import { SelectAutocomplete } from '@shared/inputs/SelectAutocomplete';
import { IOption } from '@shared/interfaces';
import { useServerSide } from '@shared/hooks/useServerSide';

import { ViewHeightWrapper } from '@styles/CommonStyles';
import { useTabs } from '@shared/hooks/useTabs';
import { withOpenTab } from '@shared/HOCs/withOpenTab';
import { CancelButton } from '@shared/inputs/Buttons';
import { selectGridState } from '@store/reducers/gridsSlice';
import { useAppSelector } from '@store/hooks';

import { AssignContactsModal } from './components/AssignContacts/AssignContactsModal';
import { IContactListItem, IContactsListResponse, requestAssignContacts } from './queries';

const ActionButtonsContainer = styled.div`
  display: flex;
  gap: 8px;
  height: 35px;
  justify-content: center;
  align-items: center;

  .assign-button {
    width: 250px;
  }
`;

const DEFAULT_SORT: GridSortModel = [
  {
    field: 'leadReceivedDate',
    sort: 'desc',
  },
];

const AdminToolsMainPage = () => {
  const navigate = useNavigate();
  const { openTab } = useTabs();
  const savedContactGridFilter = useAppSelector(selectGridState('All Contact'));
  const [editing, toggleEditing] = useToggle();
  
  const {
    sortModel,
    requestPayload,
    onSortModelChange,
    onFilterModelChange,
    onPageChange,
    handleClosePanel
  } = useServerSide({
    initialSortModel: savedContactGridFilter?.sorting?.sortModel ?? [],
    defaultSortModel: DEFAULT_SORT,
  });
  const [items, setItems] = useState<IContactListItem[]>([]);
  const [totalCount, setTotalCount] = useState<number>(0);
  const [assignTo, setAssignTo] = useState<ISelectOption>();
  const [assignContactsModalIsOpen, setAssignContactsModalIsOpen] = useState(false);
  const [selectionModel, setSelectionModel] = useState<GridSelectionModel>([]);
  const {
    data: contacts,
    mutate,
    isValidating,
  } = useSWR<IContactsListResponse>(
    `/${SERVICES.SEARCH}/search/contactsearch`,
    (url: string) => axios.post(url, { ...requestPayload, pageSize: 25 }),
    {
      shouldRetryOnError: false,
      revalidateOnMount: false,
    }
  );

  const memoizedRequestPayload = useMemo(
    () => requestPayload,
    [
      requestPayload.pageNumber,
      requestPayload.orderBy,
      requestPayload.orderByDirection,
      requestPayload.filters,
      requestPayload.operator,
    ]
  );
 
  useEffect(() => {
    if (memoizedRequestPayload) {
      mutate();
    }
  }, [memoizedRequestPayload]);

  const { users } = useUsers();

  useEffect(() => {
    if (contacts) {
      const { data: { items, totalCount } } = contacts;
      setItems(items || []);
      setTotalCount(totalCount || 0);
    }
  }, [contacts]);

  const userSelectOptions = useMemo(() => users?.groupUsers?.map((user) => ({
    label: user.userName,
    value: {
      value: user.userId,
      label: user.userName,
    },
  })) || [], [users]);

  const columns: GridColDef[] = [
    dataGridCol('contactCreatedDate', {
      minWidth: 250,
      type: 'dateTime', 
      headerName: 'Contact Created Date',
      valueFormatter: gridDateTimeFormatter,
    }),
    dataGridCol('firstName'),
    dataGridCol('lastName'),
    dataGridCol('email', { minWidth: 240 }),
    dataGridCol('cellPhone', { valueGetter: ({ value }) => formatPhoneNumber(value) }),
    dataGridCol('homePhone', { valueGetter: ({ value }) => formatPhoneNumber(value) }),
    dataGridCol('workPhone', { valueGetter: ({ value }) => formatPhoneNumber(value) }),
    dataGridCol('leadReceivedDate', {
      minWidth: 240,
      type: 'dateTime', 
      headerName: 'Lead Received Date',
      valueFormatter: gridDateTimeFormatter,
    }),
    dataGridCol('source', { minWidth: 220, headerName: 'Source' }),
  ].map((column) => ({ ...column, minWidth: 120, flex: 1 }));

  const onSelectionModelChange = (model: GridSelectionModel) => {
    model && setSelectionModel(model);
  };

  const selectedContacts = useMemo(
    () => items?.filter((item) => selectionModel.includes(item.id)),
    [items, selectionModel],
  );
  
  const requiredFieldsError = useCallback(() => {
    if (!assignTo?.id && !selectionModel.length) {
      return 'Choose user and at least one contact.';
    }

    return `${!assignTo?.id ? 'Choose user.' : ''}${!selectionModel.length ? 'Choose at least one contact.' : ''}`;
  }, [assignTo, selectionModel]);

  const onNavigate = useCallback(
    (contact: IContactListItem) => {
      const route = withQueryParams('/contact-manager', { id: contact.id });
      const title = `${contact.firstName} ${contact.lastName}`;
      
      openTab({ title, route });
      navigate(route);
    },
    [],
  );

  const handleConfirmAssignContacts = useCallback(() => {
    toggleEditing();
    setSelectionModel([]);

    if (!requiredFieldsError()) {
      requestAssignContacts(assignTo?.id ?? '', selectionModel);
    } else {
      Notistack.generalError(requiredFieldsError());
    }

    setAssignTo(undefined);
    setAssignContactsModalIsOpen(false);
  }, [assignTo, selectionModel]);

  const handleCloseAssignContacts = useCallback(() => {
    setAssignContactsModalIsOpen(false);
    setAssignTo(undefined);
    setSelectionModel([]);
  }, []);

  const hadleSelectChange = useCallback((user: IOption<string>) => {
    setAssignTo({
      id: user?.value ?? '',
      name: user?.label ?? '',
    });
  }, [userSelectOptions]);
   
  const handleCancel = () => {
    setAssignTo(undefined);
    setSelectionModel([]);
    toggleEditing();
  };

  const selectedAssignedUser = useMemo(() => ({
    value: assignTo?.id || '',
    label: assignTo?.name || '',
  }), [assignTo]);

  const buttonsGroup = editing ? (
    <ActionButtonsContainer>
      <SelectAutocomplete
        fullWidth
        value={selectedAssignedUser}
        options={userSelectOptions}
        placeholder="Select User"
        onChange={hadleSelectChange}
      />

      <Button
        className="assign-button"
        variant="contained"
        color="primary"
        disabled={!(assignTo && selectionModel.length)}
        onClick={() => setAssignContactsModalIsOpen(true)}
      >
        Assign Selected
      </Button>

      <CancelButton variant="contained" onClick={handleCancel}/>
    </ActionButtonsContainer>
  ) : (
    <Button
      color="primary"
      variant="contained"
      onClick={toggleEditing}>
      Assign Contacts
    </Button>
  );
  
  const onAddContact = () => {
    const route = '/admin-tools/add-new-contact';
    const title = 'Create Contact';

    openTab({ title, route });
    navigate(route);
  };

  return (
    <ViewHeightWrapper>
      <PhoenixGrid
        key={Number(editing) + (assignTo?.id ?? '')}
        withHeader
        showClearAllFilter
        disableSelectionOnClick
        name="All Contact"
        buttonTitle="Add New Contact"
        headerContent={buttonsGroup}
        pageSize={25}
        columns={columns}
        rows={items && !isValidating ? items : []}
        loading={!items || isValidating}
        selectionModel={selectionModel}
        checkboxSelection={editing}
        rowCount={totalCount}
        paginationMode='server'
        sortingMode='server'
        filterMode='server'
        sortModel={sortModel}
        defaultSortModel={DEFAULT_SORT}
        onButtonClick={onAddContact}
        onSelectionModelChange={onSelectionModelChange}
        onSortModelChange={onSortModelChange}
        onFilterModelChange={onFilterModelChange}
        onPageChange={onPageChange}
        onPreferencePanelClose={handleClosePanel}
        onRowClick={({ row }: { row: IContactListItem }) => onNavigate(row)}
      />

      {assignContactsModalIsOpen && (
        <AssignContactsModal
          handleClose={handleCloseAssignContacts}
          handleConfirm={handleConfirmAssignContacts}
          selectedContacts={selectedContacts}
          selectedUser={assignTo}
        />
      )}
    </ViewHeightWrapper>
  );
};

export const AdminToolsMain = withOpenTab(AdminToolsMainPage, 'Main');
